import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../LanguageSwitcher/LanguageSwitcher";
import { Link, useLocation } from "react-router-dom";

import styles from "./Hero.module.css";

const slides = [
  { type: "image", src: "/images/slide1-1920.webp" },
  { type: "image", src: "/images/slide2-1920.webp" },
  { type: "image", src: "/images/slide3-1920.webp" },
  { type: "image", src: "/images/slide4-1920.webp" },
  { type: "image", src: "/images/slide5-1920.webp" },
  { type: "image", src: "/images/slide6-1920.webp" },
  { type: "image", src: "/images/slide7-1920.webp" },
  { type: "image", src: "/images/slide8-1920.webp" },
  { type: "image", src: "/images/slide9-1920.webp" },
  { type: "image", src: "/images/slide10-1920.webp" },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const headerRef = useRef(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const autoplayRef = useRef(null);

  const { t } = useTranslation();
  const location = useLocation();

  const goToNextSlide = useCallback(() => {
  setCurrent((prev) => (prev + 1) % slides.length);
	}, []);

	const goToPrevSlide = useCallback(() => {
	  setCurrent((prev) => (prev - 1 + slides.length) % slides.length);
	}, []);

	const resetAutoplay = useCallback(() => {
	  if (autoplayRef.current) {
		clearInterval(autoplayRef.current);
	  }

	  autoplayRef.current = setInterval(() => {
		setCurrent((prev) => (prev + 1) % slides.length);
	  }, 5000);
	}, []);

  useEffect(() => {
  resetAutoplay();

  return () => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
  };
}, [resetAutoplay]);

useEffect(() => {
  const handleKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      goToPrevSlide();
      resetAutoplay();
    }

    if (e.key === "ArrowRight") {
      goToNextSlide();
      resetAutoplay();
    }
  };

  window.addEventListener("keydown", handleKeyDown);

  return () => {
    window.removeEventListener("keydown", handleKeyDown);
  };
}, [goToNextSlide, goToPrevSlide, resetAutoplay]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 769 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const onScroll = () => {
      if (mobileMenuOpen) return;

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
            setIsVisible(false);
          } else {
            setIsVisible(true);
          }

          lastScrollY.current = currentScrollY;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [mobileMenuOpen]);

  const revealHeader = () => {
    setIsVisible(true);
  };

  const toggleMobileMenu = () => {
    setIsVisible(true);
    setMobileMenuOpen((prev) => !prev);
  };

  const navigationItems = [
    { label: t("nav.home"), href: "/", isHash: false },
    { label: t("nav.about"), href: "/about", isHash: false },
    { label: t("nav.module"), href: "#module", isHash: true },
    { label: t("nav.contact"), href: "#contact", isHash: true },
  ];

  const handleNavClick = (e, item) => {
    if (item.isHash) {
      e.preventDefault();

      if (location.pathname !== "/") {
        window.location.href = "/" + item.href;
        return;
      }

      const target = document.querySelector(item.href);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    setMobileMenuOpen(false);
  };

  const isActiveLink = (item) => {
    if (item.isHash) return false;
    return location.pathname === item.href;
  };

  return (
    <section className={styles.heroWrapper}>
      <div
        className={styles.topHoverZone}
        onMouseEnter={revealHeader}
        onTouchStart={revealHeader}
      />

      <header
        ref={headerRef}
        className={`${styles.header} ${
          !isVisible ? styles.headerHidden : ""
        }`}
        role="banner"
        aria-label="Main navigation"
      >
        <div className={styles.container}>
          <div className={styles.logo}>
            <Link to="/" aria-label="Go to homepage">
              <span className={styles.logoImage} />
            </Link>
          </div>

          <nav className={styles.desktopNav} aria-label="Desktop navigation">
            <ul className={styles.navList}>
              {navigationItems.map((item) => (
                <li key={item.label} className={styles.navItem}>
                  {item.isHash ? (
                    <a
                      href={item.href}
                      className={styles.navLink}
                      onClick={(e) => handleNavClick(e, item)}
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      to={item.href}
                      className={`${styles.navLink} ${
                        isActiveLink(item) ? styles.active : ""
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          <div className={styles.desktopLanguageSwitcher}>
            <LanguageSwitcher />
          </div>

          <button
            className={`${styles.mobileMenuButton} ${
              mobileMenuOpen ? styles.open : ""
            }`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <span className={styles.bar}></span>
            <span className={styles.bar}></span>
            <span className={styles.bar}></span>
          </button>
        </div>

        <div
          id="mobile-menu"
          className={`${styles.mobileMenuOverlay} ${
            mobileMenuOpen ? styles.open : ""
          }`}
          aria-hidden={!mobileMenuOpen}
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className={styles.mobileMenuContent}
            onClick={(e) => e.stopPropagation()}
          >
            <nav className={styles.mobileNav} aria-label="Mobile navigation">
              <ul className={styles.mobileNavList}>
                {navigationItems.map((item, index) => (
                  <li
                    key={item.label}
                    className={styles.mobileNavItem}
                    style={{ "--item-index": index }}
                  >
                    {item.isHash ? (
                      <a
                        href={item.href}
                        className={styles.mobileNavLink}
                        onClick={(e) => handleNavClick(e, item)}
                      >
                        {item.label}
                      </a>
                    ) : (
                      <Link
                        to={item.href}
                        className={`${styles.mobileNavLink} ${
                          isActiveLink(item) ? styles.active : ""
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>

              <div className={styles.mobileLanguageSwitcher}>
                <LanguageSwitcher
                  variant="mobile"
                  onChange={() => setMobileMenuOpen(false)}
                />
              </div>
            </nav>
          </div>
        </div>
      </header>

      <div className={styles.sliderContainer}>
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${styles.slide} ${
              index === current ? styles.activeSlide : ""
            }`}
          >
            <div className={styles.mediaWrapper}>
              {slide.type === "image" && (
                <img
                  src={slide.src}
                  alt={`slide-${index + 1}`}
                  className={styles.slideMedia}
                />
              )}

              {slide.type === "video" && (
                <video
                  src={slide.src}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className={styles.slideMedia}
                />
              )}
            </div>
          </div>
        ))}

        <button
          type="button"
          className={`${styles.sliderArrow} ${styles.prevArrow}`}
          onClick={() => {
            goToPrevSlide();
            resetAutoplay();
          }}
          aria-label="Previous slide"
        >
          &lt;
        </button>

        <button
          type="button"
          className={`${styles.sliderArrow} ${styles.nextArrow}`}
          onClick={() => {
            goToNextSlide();
            resetAutoplay();
          }}
          aria-label="Next slide"
        >
          &gt;
        </button>
      </div>
    </section>
  );
}